import { componentHasDefinedNominal } from 'features/assetModeling'
import LossByPercentageDataWorker from 'features/dataLayers/generation/coreLayers/wastage/calculateLossByPercentageData.worker?worker'
import { canCreateDfnCategoryLayer } from 'features/dataLayers/generation/dfnCategory/dfnCategoryLayer'
import DfnCategoryWorker from 'features/dataLayers/generation/workers/calculateDfnCategoryData.worker?worker'
import EmatCombinedCategoryWorker from 'features/dataLayers/generation/workers/calculateEmatCombinedCategoryData.worker?worker'
import LineAverageWorker from 'features/dataLayers/generation/workers/calculateLineAveragedThickness.worker?worker'
import LossDataWorker from 'features/dataLayers/generation/workers/calculateLossData.worker?worker'
import LossPerYearDataWorker from 'features/dataLayers/generation/workers/calculateLossPerYearData.worker?worker'
import LossPerYearWithCoating from 'features/dataLayers/generation/workers/calculateLossPerYearWithCoatingData.worker?worker'
import RcaDataWorker from 'features/dataLayers/generation/workers/calculateRcaData.worker?worker'
import RCAPercentDataWorker from 'features/dataLayers/generation/workers/calculateRcaPercentData.worker?worker'
import RemainingLifeDataWorker from 'features/dataLayers/generation/workers/calculateRemainingLifeData.worker?worker'
import RemainingLifeWithCoatingDataWorker from 'features/dataLayers/generation/workers/calculateRemainingLifeDataCoating.worker?worker'
import ShipDeckPITThicknessWorker from 'features/dataLayers/generation/workers/calculateShipDeckPITThickness.worker?worker'
import ThicknessByMeanWorker from 'features/dataLayers/generation/workers/calculateThicknessByMeanData.worker?worker'
import TimeSinceLastRepairWorker from 'features/dataLayers/generation/workers/calculateTimeSinceLastRepairData.worker?worker'
import TminDataWorker from 'features/dataLayers/generation/workers/calculateTminData.worker?worker'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import {
  ForecastComponentConfig,
  GeckoAggregation,
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoInspection,
  GeckoMeasurementUnit,
  GeckoSource,
  GeckoTMinMethod,
  GeckoView,
  PredictedInspectionType,
} from 'types'

const getInstallationDate = (ram: GeckoAssetModel | undefined, cId: string) => {
  //method to get the installation date - if the component has an
  //installation date use that otherwise use the asset model installation date
  const componentRam = ram?.data.components.get(cId)
  if (componentRam && 'installationDate' in componentRam)
    return componentRam?.installationDate ?? ram?.data.installation_date
  return ram?.data.installation_date
}

export class FdeCdfSpecification {
  //-----------------------------------------------------------------------------

  public static readonly Description = 'A specifier for what CDF to generate'

  // - - - - - - - - - - - - - - - - Tmin - - - - - - - - - - - - - - - -

  public static readonly TminByShellCourse = new FdeCdfSpecification(
    //this is basically tmin by feature
    GeckoSource.TMin,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinByShellCourse,
    GeckoMeasurementUnit.Millimeters,
    (ram, cId) => {
      const componentRAM = ram?.data.components.get(cId)
      if (
        componentRAM?.type !== GeckoComponentModelType.VerticalTankShell &&
        componentRAM?.type !== GeckoComponentModelType.LegacyCone
      ) {
        return false
      }
      //ensure that every shell course has a tmin value
      return (
        componentRAM.coursesArray.every((course) => {
          return !isNil(course.tmin)
        }) && componentRAM.coursesArray.length > 0
      )
    },
    () => new TminDataWorker()
  )

  public static readonly TminByApi653 = new FdeCdfSpecification(
    GeckoSource.TMin,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinByApi653,
    GeckoMeasurementUnit.Millimeters,
    (ram, componentId) => {
      const cRam = ram?.data.components.get(componentId)
      if (
        cRam?.type !== GeckoComponentModelType.VerticalTankShell ||
        ram?.data.type !== GeckoAssetModelType.VerticalTank
      )
        return false

      const isStressToleranceValid =
        //either we have both upper and lower courses stress tolerance or we have a stress tolerance for every course
        (!isNil(cRam.stressToleranceUpperShellCourses) &&
          !isNil(cRam.stressToleranceLowerShellCourses)) ||
        cRam.coursesArray.every((sc) => !!sc.stressTolerance)

      return (
        !isNil(ram?.data.fillElevation) &&
        !isNil(ram?.data.specificGravity) &&
        !isNil(cRam.jointEfficiency) &&
        isStressToleranceValid &&
        !isNil(cRam.radius)
      )
    },
    () => new TminDataWorker()
  )
  public static readonly TminWithAnnularRing = new FdeCdfSpecification(
    GeckoSource.TMin,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinWithAnnularRing,
    GeckoMeasurementUnit.Millimeters,
    (ram, cId) => {
      const cRam = ram?.data.components.get(cId)
      if (cRam?.type !== GeckoComponentModelType.TankFloor) return false
      const annularRings = cRam.annularRingsArray
      return (
        annularRings.length > 0 &&
        annularRings.every((ring) => {
          return !isNil(ring.tmin)
        }) &&
        !isNil(cRam.tmin)
      )
    },
    () => new TminDataWorker()
  )
  public static readonly TminByApi570 = new FdeCdfSpecification(
    GeckoSource.TMin,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinByApi570,
    GeckoMeasurementUnit.Millimeters,
    (ram, cId, fcc) => {
      const cRam = ram?.data.components.get(cId)
      if (cRam?.type !== GeckoComponentModelType.PipeSection) return false

      const missingPressure =
        isNil(ram?.data.operatingPressure) &&
        isNil(cRam.designPressure) &&
        isNil(fcc.operation_pressure)

      const missingAllowableStress =
        isNil(cRam.allowableStress) && isNil(fcc.allowable_unit_stress)

      return (
        !isNil(cRam.outerDiameter) &&
        !missingPressure &&
        !missingAllowableStress &&
        !isNil(cRam.jointEfficiency)
      )
    },
    () => new TminDataWorker()
  )
  public static readonly TminByApi510 = new FdeCdfSpecification(
    GeckoSource.TMin,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinByApi510,
    GeckoMeasurementUnit.Millimeters,
    (ram, cId) => {
      const cRam = ram?.data.components.get(cId)

      switch (cRam?.type) {
        case GeckoComponentModelType.PressureVesselShell:
          return (
            !isNil(cRam.jointEfficiency) &&
            !isNil(cRam.stressTolerance) &&
            !isNil(cRam.designPressure) &&
            !isNil(cRam.radius)
          )
        case GeckoComponentModelType.HemisphericalHead:
          return (
            !isNil(cRam.jointEfficiency) &&
            !isNil(cRam.stressTolerance) &&
            !isNil(cRam.designPressure) &&
            !isNil(cRam.radius)
          )
        case GeckoComponentModelType.EllipsoidalHead:
          return (
            !isNil(cRam.jointEfficiency) &&
            !isNil(cRam.stressTolerance) &&
            !isNil(cRam.designPressure) &&
            !isNil(cRam.radius) &&
            !isNil(cRam.minorRadius)
          )
        case GeckoComponentModelType.TorisphericalHead:
          return (
            !isNil(cRam.jointEfficiency) &&
            !isNil(cRam.stressTolerance) &&
            !isNil(cRam.designPressure) &&
            !isNil(cRam.knuckleRadius) &&
            !isNil(cRam.crownRadius)
          )
        default:
          return false
      }
    },
    () => new TminDataWorker()
  )

  public static readonly TminByComponent = new FdeCdfSpecification(
    GeckoSource.TMin,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinByComponent,
    GeckoMeasurementUnit.Millimeters,
    (ram, cId) => {
      const cRam = ram?.data.components.get(cId)
      if (!cRam || !('tmin' in cRam)) {
        return false
      }
      return !isNil(cRam.tmin)
    },
    () => new TminDataWorker()
  )

  public static readonly TminByShipDeck = new FdeCdfSpecification(
    GeckoSource.TMin,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinByShipDeck,
    GeckoMeasurementUnit.Millimeters,
    (ram, cId) => {
      const cRam = ram?.data.components.get(cId)
      if (cRam?.type !== GeckoComponentModelType.ShipDeck) return false

      // For ship decks, we don't need a fixed tmin value since it's calculated as 75% of nominal
      return true
    },
    () => new TminDataWorker()
  )

  public static readonly TminByWall = new FdeCdfSpecification(
    GeckoSource.TMin,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinByWall,
    GeckoMeasurementUnit.Millimeters,
    (ram, cId) => {
      const cRam = ram?.data.components.get(cId)
      if (cRam?.type !== GeckoComponentModelType.LegacyBoilerWalls) return false
      return cRam.wallsArray.every((wall) => {
        return !isNil(wall.tmin)
      })
    },
    () => new TminDataWorker()
  )

  // - - - - - - - - - - - - - - - Loss - - - - - - - - - - - - - - -
  public static readonly Loss = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.DifferenceFromNominalProjection,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.Millimeters,
    (assetModel, cId) => {
      if (isNil(assetModel)) return false
      const cRam = assetModel.data.components.get(cId)
      if (isNil(cRam)) return false
      if (cRam.type === GeckoComponentModelType.BasicShipHull) return false
      return componentHasDefinedNominal(cRam)
    },
    () => new LossDataWorker()
  )
  public static readonly LossByPercentage = new FdeCdfSpecification(
    GeckoSource.RAUT,
    GeckoView.DifferenceFromNominalProjection,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.Percent,
    (ram, cId, fcc, insp) =>
      FdeCdfSpecification.Loss.canCreateCdfData(ram, cId, fcc, insp) &&
      !isNil(insp) &&
      insp.data.inspection_type === 'raut',
    () => new LossByPercentageDataWorker()
  )
  public static readonly LossPerYear = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.LongTermCorrosionRate,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.MillimetersPerYear,
    (ram, cId, fcc, insp) => {
      const installation_date = getInstallationDate(ram, cId)
      const lossCanCreate = FdeCdfSpecification.Loss.canCreateCdfData(
        ram,
        cId,
        fcc,
        insp
      )

      return lossCanCreate && !isNil(installation_date)
    },
    () => new LossPerYearDataWorker()
  )
  public static readonly LossPerYearWithCoating = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.LongTermCorrosionRateWithHistoricalCoating,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.MillimetersPerYear,
    (ram, cId, fcc, insp) => {
      const cRam = ram?.data.components.get(cId)
      if (cRam?.type !== GeckoComponentModelType.VerticalTankShell) return false
      const installation_date = getInstallationDate(ram, cId)
      return (
        FdeCdfSpecification.Loss.canCreateCdfData(ram, cId, fcc, insp) &&
        !isNil(installation_date) &&
        (!isEmpty(cRam.coatingRegions) || !isEmpty(cRam.linerRegions))
      )
    },
    () => new LossPerYearWithCoating()
  )

  // - - - - - - - - - - Remaining Corrosion Allowance - - - - - - - - - -
  public static readonly RcaByShellCourse = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowance,
    GeckoTMinMethod.TMinByShellCourse,
    GeckoMeasurementUnit.Millimeters,
    FdeCdfSpecification.TminByShellCourse.canCreateCdfData,
    () => new RcaDataWorker()
  )

  public static readonly RcaByShellCoursePercent = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowancePercent,
    GeckoTMinMethod.TMinByShellCourse,
    GeckoMeasurementUnit.Percent,
    FdeCdfSpecification.TminByShellCourse.canCreateCdfData,
    () => new RCAPercentDataWorker()
  )

  public static readonly RcaByApi653 = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowance,
    GeckoTMinMethod.TMinByApi653,
    GeckoMeasurementUnit.Millimeters,
    FdeCdfSpecification.TminByApi653.canCreateCdfData,
    () => new RcaDataWorker()
  )

  public static readonly RcaByApi653Percent = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowancePercent,
    GeckoTMinMethod.TMinByApi653,
    GeckoMeasurementUnit.Percent,
    FdeCdfSpecification.TminByApi653.canCreateCdfData,
    () => new RCAPercentDataWorker()
  )

  public static readonly RCAWithAnnularRing = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowance,
    GeckoTMinMethod.TMinWithAnnularRing,
    GeckoMeasurementUnit.Millimeters,
    FdeCdfSpecification.TminWithAnnularRing.canCreateCdfData,
    () => new RcaDataWorker()
  )
  public static readonly RcaByApi570 = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowance,
    GeckoTMinMethod.TMinByApi570,
    GeckoMeasurementUnit.Millimeters,
    FdeCdfSpecification.TminByApi570.canCreateCdfData,
    () => new RcaDataWorker()
  )
  public static readonly RcaByApi510 = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowance,
    GeckoTMinMethod.TMinByApi510,
    GeckoMeasurementUnit.Millimeters,
    FdeCdfSpecification.TminByApi510.canCreateCdfData,
    () => new RcaDataWorker()
  )

  public static readonly RCAByComponent = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowance,
    GeckoTMinMethod.TMinByComponent,
    GeckoMeasurementUnit.Millimeters,
    FdeCdfSpecification.TminByComponent.canCreateCdfData,
    () => new RcaDataWorker()
  )

  public static readonly RCAByShipDeck = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowance,
    GeckoTMinMethod.TMinByShipDeck,
    GeckoMeasurementUnit.Millimeters,
    FdeCdfSpecification.TminByShipDeck.canCreateCdfData,
    () => new RcaDataWorker()
  )

  public static readonly RCAByWall = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingCorrosionAllowance,
    GeckoTMinMethod.TMinByWall,
    GeckoMeasurementUnit.Millimeters,
    FdeCdfSpecification.TminByWall.canCreateCdfData,
    () => new RcaDataWorker()
  )

  // - - - - - - - - - - - - Remaining Life - - - - - - - - - - - -
  public static readonly RlByShellCourse = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLife,
    GeckoTMinMethod.TMinByShellCourse,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      FdeCdfSpecification.LossPerYear.canCreateCdfData(ram, cId, fcc, insp) &&
      FdeCdfSpecification.RcaByShellCourse.canCreateCdfData(
        ram,
        cId,
        fcc,
        insp
      ),
    () => new RemainingLifeDataWorker()
  )
  public static readonly RlByApi653 = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLife,
    GeckoTMinMethod.TMinByApi653,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      FdeCdfSpecification.LossPerYear.canCreateCdfData(ram, cId, fcc, insp) &&
      FdeCdfSpecification.RcaByApi653.canCreateCdfData(ram, cId, fcc, insp),
    () => new RemainingLifeDataWorker()
  )

  public static readonly RlByShellCourseWithCoating = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLifeWithHistoricalCoating,
    GeckoTMinMethod.TMinByApi653,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      FdeCdfSpecification.LossPerYearWithCoating.canCreateCdfData(
        ram,
        cId,
        fcc,
        insp
      ) &&
      FdeCdfSpecification.RcaByShellCourse.canCreateCdfData(
        ram,
        cId,
        fcc,
        insp
      ),
    () => new RemainingLifeWithCoatingDataWorker()
  )

  public static readonly RlByApi653WithCoating = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLifeWithHistoricalCoating,
    GeckoTMinMethod.TMinByApi653,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      FdeCdfSpecification.LossPerYearWithCoating.canCreateCdfData(
        ram,
        cId,
        fcc,
        insp
      ) &&
      FdeCdfSpecification.RcaByApi653.canCreateCdfData(ram, cId, fcc, insp),
    () => new RemainingLifeWithCoatingDataWorker()
  )

  public static readonly RlWithAnnularRing = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLife,
    GeckoTMinMethod.TMinWithAnnularRing,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      FdeCdfSpecification.LossPerYear.canCreateCdfData(ram, cId, fcc, insp) &&
      FdeCdfSpecification.RCAWithAnnularRing.canCreateCdfData(
        ram,
        cId,
        fcc,
        insp
      ),
    () => new RemainingLifeDataWorker()
  )

  public static readonly RlWithTminByComponent = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLife,
    GeckoTMinMethod.TMinByComponent,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      FdeCdfSpecification.LossPerYear.canCreateCdfData(ram, cId, fcc, insp) &&
      FdeCdfSpecification.RCAByComponent.canCreateCdfData(ram, cId, fcc, insp),
    () => new RemainingLifeDataWorker()
  )

  public static readonly RlWithTminByWall = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLife,
    GeckoTMinMethod.TMinByWall,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      FdeCdfSpecification.LossPerYear.canCreateCdfData(ram, cId, fcc, insp) &&
      FdeCdfSpecification.RCAByWall.canCreateCdfData(ram, cId, fcc, insp),
    () => new RemainingLifeDataWorker()
  )

  public static readonly RlWithTminByShipDeck = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLife,
    GeckoTMinMethod.TMinByShipDeck,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) => {
      if (isNil(ram)) {
        return false
      }
      const cRam = ram.data.components.get(cId)
      if (cRam?.type !== GeckoComponentModelType.ShipDeck) {
        return false
      }

      const lossPerYearCanCreate =
        FdeCdfSpecification.LossPerYear.canCreateCdfData(ram, cId, fcc, insp)
      const rcaByShipDeckCanCreate =
        FdeCdfSpecification.RCAByShipDeck.canCreateCdfData(ram, cId, fcc, insp)

      // For ship decks, we don't need a fixed tmin value since it's calculated as 75% of nominal
      return lossPerYearCanCreate && rcaByShipDeckCanCreate
    },
    () => new RemainingLifeDataWorker()
  )

  public static readonly LineAverageThicknessBy653 = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.Thickness,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.Inches,
    (ram, cId) => {
      if (isNil(ram)) return false
      const cRam = ram.data.components.get(cId)
      return cRam?.type === GeckoComponentModelType.VerticalTankShell
    },
    () => new LineAverageWorker()
  )
  public static readonly RlByApi570 = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLife,
    GeckoTMinMethod.TMinByApi570,
    GeckoMeasurementUnit.Years,
    (cRam, fcc, insp) =>
      FdeCdfSpecification.LossPerYear.canCreateCdfData(cRam, fcc, insp) &&
      FdeCdfSpecification.RcaByApi570.canCreateCdfData(cRam, fcc, insp),
    () => new RemainingLifeDataWorker()
  )
  public static readonly RlByApi510 = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.RemainingLife,
    GeckoTMinMethod.TMinByApi510,
    GeckoMeasurementUnit.Years,
    (cRam, fcc, insp) =>
      FdeCdfSpecification.LossPerYear.canCreateCdfData(cRam, fcc, insp) &&
      FdeCdfSpecification.RcaByApi510.canCreateCdfData(cRam, fcc, insp),
    () => new RemainingLifeDataWorker()
  )
  public static readonly RlCustomCrByShellCourse = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.CustomCRRemainingLife,
    GeckoTMinMethod.TMinByShellCourse,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      !isEmpty(fcc.cr_by_shell_course) &&
      FdeCdfSpecification.RcaByShellCourse.canCreateCdfData(
        ram,
        cId,
        fcc,
        insp
      ),
    () => new RemainingLifeDataWorker()
  )
  public static readonly RlCustomCrByApi653 = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.CustomCRRemainingLife,
    GeckoTMinMethod.TMinByApi653,
    GeckoMeasurementUnit.Years,
    (ram, cId, fcc, insp) =>
      !isEmpty(fcc.cr_by_shell_course) &&
      FdeCdfSpecification.RcaByApi653.canCreateCdfData(ram, cId, fcc, insp),
    () => new RemainingLifeDataWorker()
  )
  // - - - - - - - - - - - - - - - History - - - - - - - - - - - - - - -
  public static readonly TimeSinceLastRepair = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.TimeSinceLastRepair,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.Years,
    (ram, cid) => {
      const installationDate = getInstallationDate(ram, cid)
      return !isNil(installationDate)
    },
    () => new TimeSinceLastRepairWorker()
  )
  // - - - - - - - - - - - - - - - - HHUT - - - - - - - - - - - - - - - -
  public static readonly HHUTThicknessByMean = new FdeCdfSpecification(
    GeckoSource.Manual,
    GeckoView.Thickness,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.Inches,
    (ram, cId, fcc, insp) => {
      return !isNil(insp) && insp.data.inspection_type === 'manual'
    },
    () => new ThicknessByMeanWorker(),
    GeckoAggregation.ComponentMean
  )
  // - - - - - - - - - - - - - - - Ship Deck Specifications - - - - - - - - - - - - - - -

  public static readonly EmatCombinedCategory = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.EmatCustomThicknessCondition,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.EmatCustomCategory,
    (assetModel, componentId, fcc, inspection) => {
      return (
        !isNil(inspection) &&
        (inspection.data.inspection_type === 'emat' ||
          PredictedInspectionType.includes(
            inspection.data.inspection_type as PredictedInspectionType
          ))
      )
    },
    () => new EmatCombinedCategoryWorker()
  )

  public static readonly DfnCategory = new FdeCdfSpecification(
    GeckoSource.RUG,
    GeckoView.DfnCategory,
    GeckoTMinMethod.None,
    GeckoMeasurementUnit.DfnCategory,
    (assetModel, componentId, _forecastComponentConfig, inspection) =>
      canCreateDfnCategoryLayer(assetModel, componentId, inspection),
    () => new DfnCategoryWorker()
  )

  // - - - - - - - - - - - - - - - Predicted - - - - - - - - - - - - - - -
  public static readonly PredictedThickness = new FdeCdfSpecification(
    GeckoSource.Projection,
    GeckoView.Thickness,
    GeckoTMinMethod.TMinByShipDeck,
    GeckoMeasurementUnit.Millimeters,
    (assetModel, componentId, fcc, insp) => {
      return FdeCdfSpecification.LossPerYear.canCreateCdfData(
        assetModel,
        componentId,
        fcc,
        insp
      )
    },
    () => new ShipDeckPITThicknessWorker()
  )

  //-------------------------------- Properties --------------------------------
  public readonly source: GeckoSource
  public readonly view: GeckoView
  public readonly tminMethod: GeckoTMinMethod
  public readonly aggregation: GeckoAggregation | undefined
  public readonly defaultMeasurementUnit: GeckoMeasurementUnit
  public readonly canCreateCdfData: (
    assetModel: GeckoAssetModel | undefined,
    componentId: string,
    fcc: ForecastComponentConfig,
    insp?: GeckoInspection
  ) => boolean
  public readonly getWorkerFile: () => Worker

  // ------------------------------- Constructor -------------------------------
  private constructor(
    source: GeckoSource,
    view: GeckoView,
    tminMethod: GeckoTMinMethod,
    defaultMeasurementUnit: GeckoMeasurementUnit,
    canCreateCdfData: (
      ram: GeckoAssetModel | undefined,
      componentId: string,
      fcc: ForecastComponentConfig,
      insp?: GeckoInspection,
      component?: GeckoComponentModel
    ) => boolean,
    getWorkerFile: () => Worker,
    aggregation?: GeckoAggregation
  ) {
    this.source = source
    this.view = view
    this.tminMethod = tminMethod
    this.aggregation = aggregation
    this.defaultMeasurementUnit = defaultMeasurementUnit
    this.canCreateCdfData = canCreateCdfData
    this.getWorkerFile = getWorkerFile
  }
}

export const FdeCdfSpecificationList: readonly FdeCdfSpecification[] = [
  FdeCdfSpecification.Loss, // Loss
  FdeCdfSpecification.LossPerYear, // Loss Per Year]
  FdeCdfSpecification.LossPerYearWithCoating, // Loss Per Year With Coating
  FdeCdfSpecification.RlByShellCourseWithCoating,
  FdeCdfSpecification.RlByApi653WithCoating,
  FdeCdfSpecification.TminByShellCourse, // TMin by Shell Course
  FdeCdfSpecification.TminWithAnnularRing, // TMin with Annular Ring
  FdeCdfSpecification.TminByApi653, // TMin by API653
  FdeCdfSpecification.TminByApi570, // TMin by API570
  FdeCdfSpecification.TminByApi510, // TMin by API510
  FdeCdfSpecification.TminByComponent, //Tmin by component for Floors and Roofs
  FdeCdfSpecification.TminByWall, // Tmin by Wall - used for Boiler walls
  FdeCdfSpecification.TminByShipDeck, // Tmin by Ship Deck
  FdeCdfSpecification.RcaByShellCourse, // Remaining Corrosion Allowance by Shell Course
  FdeCdfSpecification.RcaByShellCoursePercent, // Remaining Corrosion Allowance by API570 Percent
  FdeCdfSpecification.RcaByApi653, // Remaining Corrosion Allowance by API653
  FdeCdfSpecification.RcaByApi653Percent, // Remaining Corrosion Allowance by API653 Percent
  FdeCdfSpecification.RCAWithAnnularRing, // Remaining Corrosion Allowance with Annular Ring
  FdeCdfSpecification.RcaByApi570, // Remaining Corrosion Allowance by API570
  FdeCdfSpecification.RcaByApi510, // Remaining Corrosion Allowance by API510
  FdeCdfSpecification.RCAByComponent, // Remaining Corrosion Allowance by fixed tmin for the component - used in roofs and floors
  FdeCdfSpecification.RCAByShipDeck, // Remaining Corrosion Allowance by fixed tmin for the ship deck
  FdeCdfSpecification.RCAByWall, // Remaining Corrosion Allowance by fixed tmin for the wall - used in boiler walls
  FdeCdfSpecification.RlByShellCourse, // Remaining Life by Shell Course
  FdeCdfSpecification.RlWithAnnularRing, //Remaining Life with Annular ring
  FdeCdfSpecification.RlByApi653, // Remaining Life by API653
  FdeCdfSpecification.RlByApi570, // Remaining Life by API570
  FdeCdfSpecification.RlByApi510, // Remaining Life by API510
  FdeCdfSpecification.RlWithTminByComponent, // Remaining Life by fixed tmin for the component - used in roofs and floors
  FdeCdfSpecification.RlWithTminByWall, // Remaining Life by fixed tmin for the wall - used in boiler walls
  FdeCdfSpecification.RlWithTminByShipDeck, // Remaining Life by fixed tmin for the ship deck
  FdeCdfSpecification.RlCustomCrByShellCourse, // Remaining Life Custom CR by Shell Course
  FdeCdfSpecification.RlCustomCrByApi653, // Remaining Life Custom CR by API653
  FdeCdfSpecification.TimeSinceLastRepair, // Time Since Last Repair
  FdeCdfSpecification.LossByPercentage, // RAUT Loss By Percentage
  FdeCdfSpecification.EmatCombinedCategory, // EMAT Combined Category
  FdeCdfSpecification.DfnCategory,
]
export const CommercialFdeCdfSpecificationList: readonly FdeCdfSpecification[] =
  FdeCdfSpecificationList.filter(
    (spec) =>
      spec !== FdeCdfSpecification.EmatCombinedCategory &&
      spec !== FdeCdfSpecification.DfnCategory
  )
